<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { Link, navigate } from 'svelte-routing';
  import { Chat, ChatInput } from 'tv2no-chat';
  import queryString from 'query-string';
  import Channels from '../Components/Chat/Channels/Channels.svelte';
  import Moderator from '../Components/Chat/Moderator/Moderator.svelte';
  import { addAdminComment, changeChannelStatus, listenToChannel } from '../Api/api';
  import {
    applicationConfig,
    firebaseApp,
    selectedChannel,
    selectedChannelId,
    selectedCategory,
    user,
    singleChatId,
  } from '../Store/store';
  import { Tab, TabList, TabPanel, Tabs } from '../Components/Common/Tabs';
  import AdminChat from '../Components/Chat/AdminChat/AdminChat.svelte';
  import Stats from '../Components/Chat/Stats/Stats.svelte';
  import ChatConfig from '../Components/Chat/ChatConfig/ChatConfig.svelte';
  import AppHeader from '../AppHeader.svelte';
  import { broadcaster } from '../Components/Chat/Broadcast/broadcast-store';
  import Button from '../Components/Common/Button.svelte';

  const dispatch = createEventDispatcher();

  export let hlsURL = '';
  export let channelId = '';

  let mql;
  let mediaMatchMinWidth640;
  let unsubscribeListenToChannel;
  let tabComponent;
  let selectedTab;

  $: channelId && listenToChannelData(channelId);
  $: !channelId && nullChannel();

  const nullChannel = () => {
    selectedChannelId.set('');
    selectedChannel.set(null);
    selectedCategory.set(undefined);
  };

  onMount(() => {
    mql = window.matchMedia('(min-width: 640px)');
    mediaMatchMinWidth640 = mql.matches;
    mql.addEventListener('change', onScreenChange);
  });

  onDestroy(() => {
    mql.removeEventListener('change', onScreenChange);
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
    }
  });

  const listenToChannelData = async (channelId) => {
    selectedChannelId.set(channelId);
    if (unsubscribeListenToChannel) {
      unsubscribeListenToChannel();
      unsubscribeListenToChannel = null;
    }
    unsubscribeListenToChannel = await listenToChannel(channelId, (docRef) => {
      if (docRef.exists) {
        const channel = { ...docRef.data(), id: docRef.id };
        selectedChannel.set(channel);
      }
    });
  };

  const onScreenChange = (e) => {
    mediaMatchMinWidth640 = e.matches;
  };

  const onChannelClicked = (e) => {
    selectedCategory.set(e.detail.channel.category);
    if (
      $broadcaster.isBroadcasting ||
      $broadcaster.isBroadcastingAsGuest ||
      $broadcaster.isInitializingPublish
    ) {
      return;
    }
    navigate(`/chat/edit/${e.detail.channel.id}`);
  };

  const onTabClicked = (tabTitle) => {
    dispatch('tabclicked', tabTitle);
    window.history.replaceState(null, null, `${window.location.pathname}?tab=${tabTitle}`);
  };

  const onChannelArchived = (e) => {
    selectedChannel.set(null);
    selectedChannelId.set('');
  };

  const onAddAdminComment = (e) => {
    if (e.detail) {
      addComment(e.detail);
    }
  };

  const addComment = async (chat) => {
    if (chat && $user && channelId) {
      await addAdminComment($user, channelId, chat);
    }
  };

  const setActiveTab = () => {
    if (typeof window !== 'undefined') {
      let parsed = queryString.parse(window.location.search);
      if (parsed && parsed.tab) {
        selectedTab = parsed.tab;
        tabComponent.selectTab(parsed.tab);
      }
    }
  };

  $: tabComponent && setActiveTab();

  const onGoLiveClicked = async (e, channel) => {
    e.stopPropagation();
    await changeChannelStatus(channel.id, !channel.live);
  };
</script>

{#if mediaMatchMinWidth640 || (!mediaMatchMinWidth640 && (!channelId || $singleChatId))}
  <AppHeader />
{/if}
<div class="container">
  {#if $singleChatId}
    <div class="workspace">
      <div class="workspace-container">
        <div class="tabs-container">
          <Tabs bind:this={tabComponent}>
            <div class="tab-list">
              <TabList>
                <Tab main={true} on:tabclicked={() => onTabClicked('answer')} type="answer">
                  <div class="tab-content">
                    <span class="material-icons icon"> question_answer </span>
                  </div>
                </Tab>
                <Tab main={true} on:tabclicked={() => onTabClicked('chat')} type="chat">
                  <div class="tab-content">
                    <span class="material-icons icon"> chat </span>
                  </div>
                </Tab>
              </TabList>
            </div>
            <TabPanel>
              <div class="admin-chat">
                <AdminChat id={channelId} minWidth640={mediaMatchMinWidth640} />
              </div>
            </TabPanel>
            <TabPanel>
              <div class="mobile-chat">
                <header>
                  <span class="title">publisert</span>
                  <div class="open-chat">
                    {#if $selectedChannel}
                      <Link to={`chat/${$selectedChannel.id}`}>
                        <span class="material-icons"> open_in_new </span>
                      </Link>
                    {/if}
                  </div>
                </header>
                <div class="chat-container">
                  {#if $firebaseApp && $applicationConfig.playURL}
                    <div class="chat">
                      <Chat
                        id={channelId}
                        forceWebRTC={true}
                        inputVisible={false}
                        muteAudio={true}
                        app={$firebaseApp}
                        websocketURL={$applicationConfig.playURL}
                        {hlsURL}
                      />
                    </div>
                    <div class="chat-input" class:video={$selectedChannel?.roomId}>
                      <ChatInput
                        channel={$selectedChannel}
                        on:addcomment={onAddAdminComment}
                        hostedInAdmin={true}
                      />
                    </div>
                  {/if}
                </div>
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </div>
  {:else if !mediaMatchMinWidth640}
    {#if !channelId}
      <div class="sidebar">
        <Channels on:channelclicked={onChannelClicked} />
      </div>
    {:else}
      <div class="workspace">
        {#if channelId}
          <div class="workspace-container">
            <div class="mobile-header">
              <button
                class="backBtn"
                on:click={() => navigate('/chat')}
                disabled={$broadcaster.isBroadcasting ||
                  $broadcaster.isBroadcastingAsGuest ||
                  $broadcaster.isInitializingPublish}
              >
                <span class="material-icons"> west </span>
              </button>

              {#if $selectedChannel}
                <h1>{$selectedChannel.name}</h1>
                <Button
                  on:click={(e) => onGoLiveClicked(e, $selectedChannel)}
                  big={false}
                  disabled={$selectedChannel.archived}
                  red={$selectedChannel.live}
                >
                  {$selectedChannel.live ? 'Steng chat' : 'Start chat'}
                </Button>
              {/if}
            </div>
            <div class="tabs-container">
              <Tabs bind:this={tabComponent}>
                <div class="tab-list">
                  <TabList>
                    <Tab
                      main={true}
                      on:tabclicked={() => onTabClicked('moderator')}
                      type="moderator"
                    >
                      <div class="tab-content">
                        <span class="material-icons icon"> verified </span>
                      </div>
                    </Tab>
                    <Tab main={true} on:tabclicked={() => onTabClicked('answer')} type="answer">
                      <div class="tab-content">
                        <span class="material-icons icon"> question_answer </span>
                      </div>
                    </Tab>
                    <Tab main={true} on:tabclicked={() => onTabClicked('stats')} type="stats">
                      <div class="tab-content">
                        <span class="material-icons icon"> insights </span>
                      </div>
                    </Tab>
                    <Tab main={true} on:tabclicked={() => onTabClicked('settings')} type="settings">
                      <div class="tab-content">
                        <span class="material-icons icon"> settings </span>
                      </div>
                    </Tab>
                    <Tab main={true} on:tabclicked={() => onTabClicked('chat')} type="chat">
                      <div class="tab-content">
                        <span class="material-icons icon"> chat </span>
                      </div>
                    </Tab>
                  </TabList>
                </div>
                <TabPanel>
                  <div class="moderator">
                    <Moderator id={channelId} minWidth640={mediaMatchMinWidth640} />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div class="admin-chat">
                    <AdminChat id={channelId} minWidth640={mediaMatchMinWidth640} />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div class="stats">
                    <Stats id={channelId} />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div class="config">
                    <ChatConfig id={channelId} on:channelarchived={onChannelArchived} />
                  </div>
                </TabPanel>
                <TabPanel>
                  <div class="mobile-chat">
                    <header>
                      <span class="title">publisert</span>
                      <div class="open-chat">
                        {#if $selectedChannel}
                          <Link to={`chat/${$selectedChannel.id}`}>
                            <span class="material-icons"> open_in_new </span>
                          </Link>
                        {/if}
                      </div>
                    </header>
                    <div class="chat-container">
                      {#if $firebaseApp && $applicationConfig.playURL}
                        <div class="chat">
                          <Chat
                            id={channelId}
                            forceWebRTC={true}
                            inputVisible={false}
                            muteAudio={true}
                            app={$firebaseApp}
                            websocketURL={$applicationConfig.playURL}
                            {hlsURL}
                          />
                        </div>
                        <div class="chat-input" class:video={$selectedChannel?.roomId}>
                          <ChatInput
                            channel={$selectedChannel}
                            on:addcomment={onAddAdminComment}
                            hostedInAdmin={true}
                          />
                        </div>
                      {/if}
                    </div>
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          </div>
        {:else}
          <div class="empty">
            <p>Velg ett chat rom</p>
          </div>
        {/if}
      </div>
    {/if}
  {:else}
    <div class="sidebar">
      <Channels on:channelclicked={onChannelClicked} />
    </div>
    <div class="workspace">
      {#if channelId}
        <div class="workspace-container">
          <div class="tabs-container">
            <Tabs bind:this={tabComponent}>
              <div class="tab-list">
                <TabList>
                  <Tab main={true} on:tabclicked={() => onTabClicked('moderator')} type="moderator">
                    <div class="tab-content">
                      <span class="material-icons icon"> verified </span>
                      <span>Moderering</span>
                    </div>
                  </Tab>
                  <Tab main={true} on:tabclicked={() => onTabClicked('answer')} type="answer">
                    <div class="tab-content">
                      <span class="material-icons icon"> question_answer </span>
                      <span>Svar</span>
                    </div>
                  </Tab>
                  <Tab main={true} on:tabclicked={() => onTabClicked('stats')} type="stats">
                    <div class="tab-content">
                      <span class="material-icons icon"> insights </span>
                      <span>Statistikk</span>
                    </div>
                  </Tab>
                  <Tab main={true} on:tabclicked={() => onTabClicked('settings')} type="settings">
                    <div class="tab-content">
                      <span class="material-icons icon"> settings </span>
                      <span>Innstillinger</span>
                    </div>
                  </Tab>
                </TabList>
              </div>
              <TabPanel>
                <div class="moderator">
                  <Moderator id={channelId} minWidth640={mediaMatchMinWidth640} />
                </div>
              </TabPanel>
              <TabPanel>
                <div class="admin-chat">
                  <AdminChat id={channelId} minWidth640={mediaMatchMinWidth640} />
                </div>
              </TabPanel>
              <TabPanel>
                <div class="stats">
                  <Stats id={channelId} />
                </div>
              </TabPanel>
              <TabPanel>
                <div class="config">
                  <ChatConfig id={channelId} on:channelarchived={onChannelArchived} />
                </div>
              </TabPanel>
            </Tabs>
          </div>
          <div class="preview-chat-container">
            <header>
              <span>publisert</span>
              <div class="open-chat">
                {#if $selectedChannel}
                  <Link to={`chat/${$selectedChannel.id}`}>
                    <span class="material-icons"> open_in_new </span>
                  </Link>
                {/if}
              </div>
            </header>
            {#if $firebaseApp && $applicationConfig.playURL}
              <Chat
                id={channelId}
                forceWebRTC={true}
                inputVisible={false}
                muteAudio={true}
                app={$firebaseApp}
                websocketURL={$applicationConfig.playURL}
                {hlsURL}
              />
              <div class="chat-input" class:video={$selectedChannel?.roomId}>
                <ChatInput
                  channel={$selectedChannel}
                  on:addcomment={onAddAdminComment}
                  hostedInAdmin={true}
                />
              </div>
            {/if}
          </div>
        </div>
      {:else}
        <div class="empty">
          <p>Velg ett chat rom</p>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style src="./chatAdmin.scss"></style>
